<template>
  <div
    ref="panelEl"
    v-bind="$attrs"
    class="relative flex h-full w-full flex-col border-l border-tertiary bg-secondary"
  >
    <!-- Resize handle -->
    <div
      class="resize-handle"
      @mousedown.prevent="startResize"
    />

    <!-- Tab navigation -->
    <div class="flex shrink-0 border-b border-tertiary">
      <button
        :class="[
          'flex-1 cursor-pointer truncate px-2 py-3 text-xs font-semibold transition-colors duration-150 lg:text-sm',
          activeTab === 'docs'
            ? 'border-b-2 border-accent text-accent'
            : 'text-text-main/60 hover:bg-primary/20 hover:text-text-main',
        ]"
        type="button"
        @click="activeTab = 'docs'"
      >
        Dokumentation
      </button>

      <button
        :class="[
          'flex-1 cursor-pointer truncate px-2 py-3 text-xs font-semibold transition-colors duration-150 lg:text-sm',
          activeTab === 'settings'
            ? 'border-b-2 border-accent text-accent'
            : 'text-text-main/60 hover:bg-primary/20 hover:text-text-main',
        ]"
        type="button"
        @click="activeTab = 'settings'"
      >
        Einstellungen
      </button>
    </div>

    <!-- Documentation tab -->
    <div
      v-show="activeTab === 'docs'"
      class="flex min-h-0 flex-1 flex-col"
    >
      <!-- Search -->
      <div class="shrink-0 border-b border-tertiary p-4">
        <div class="relative w-full">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Doku durchsuchen..."
            class="w-full rounded-standard border border-tertiary bg-primary p-2.5 pr-10 text-sm text-text-main placeholder:text-text-main/40 focus:outline-none focus:ring-1 focus:ring-accent"
          >

          <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <svg-icon
              type="mdi"
              :path="mdiMagnify"
              size="20"
              class="text-text-main/40"
            />
          </div>
        </div>
      </div>

      <!-- Documentation content -->
      <div class="min-h-0 flex-1 overflow-y-auto bg-primary/10">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          v-show="!noResults"
          ref="docsContainer"
          class="docs-content p-4 text-sm"
          v-html="renderedDocs"
        />

        <div
          v-if="noResults"
          class="flex h-full items-center justify-center p-4"
        >
          <p class="text-sm italic text-text-main/50">
            Keine Ergebnisse für „{{ searchQuery }}“
          </p>
        </div>
      </div>
    </div>

    <!-- Settings tab -->
    <div
      v-show="activeTab === 'settings'"
      class="flex-1 overflow-y-auto bg-primary/10 p-4"
    >
      <p class="text-sm text-text-main/60">
        Die Einstellungen werden hier in Kürze verfügbar sein...
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import MarkdownIt from 'markdown-it'
import SvgIcon from '@jamescoyle/vue-icon'
import { mdiMagnify } from '@mdi/js'
import rawDocs from '../assets/docs/Doku-api.md?raw'

const activeTab = ref<'docs' | 'settings'>('docs')
const searchQuery = ref('')
const noResults = ref(false)
const docsContainer = ref<HTMLElement | null>(null)
const panelEl = ref<HTMLElement | null>(null)

const emit = defineEmits<{
  (event: 'resize', width: number): void
}>()

const markdown = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
})

const cleanedDocs = rawDocs.replace(/<style[\s\S]*?<\/style>\s*/i, '')

const renderedDocs = computed(() => markdown.render(cleanedDocs))

// -----------------------------------------------------------------------------
// Resize
// -----------------------------------------------------------------------------

function startResize(event: MouseEvent) {
  const startX = event.clientX
  const startWidth = panelEl.value?.getBoundingClientRect().width ?? 320

  function handleMouseMove(moveEvent: MouseEvent) {
    const widthDifference = moveEvent.clientX - startX
    const newWidth = Math.round(Math.max(200, Math.min(700, startWidth - widthDifference)))

    emit('resize', newWidth)
  }

  function handleMouseUp() {
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)

    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }

  document.body.style.cursor = 'ew-resize'
  document.body.style.userSelect = 'none'

  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
}

// -----------------------------------------------------------------------------
// Search
// -----------------------------------------------------------------------------

watch(searchQuery, async (query) => {
  await nextTick()

  const container = docsContainer.value

  if (!container) {
    return
  }

  removeHighlights(container)

  const allDetails = container.querySelectorAll<HTMLDetailsElement>('details')
  const allHrs = container.querySelectorAll<HTMLHRElement>('hr')

  if (!query.trim()) {
    allDetails.forEach((detail) => {
      detail.removeAttribute('open')
      detail.style.display = ''
    })

    allHrs.forEach((hr) => {
      hr.style.display = ''
    })

    noResults.value = false
    return
  }

  const normalizedQuery = query.toLowerCase()

  allHrs.forEach((hr) => {
    hr.style.display = 'none'
  })

  allDetails.forEach((detail) => {
    const text = detail.textContent?.toLowerCase() ?? ''
    const matches = text.includes(normalizedQuery)

    detail.style.display = matches ? '' : 'none'

    if (matches) {
      detail.setAttribute('open', '')
    } else {
      detail.removeAttribute('open')
    }
  })

  const topLevelDetails = Array.from(
    container.querySelectorAll<HTMLDetailsElement>(':scope > details')
  )

  noResults.value =
    topLevelDetails.length === 0 ||
    topLevelDetails.every((detail) => detail.style.display === 'none')

  if (!noResults.value) {
    highlightText(container, query)
  }
})

function removeHighlights(container: HTMLElement) {
  container.querySelectorAll<HTMLElement>('mark.search-highlight').forEach((mark) => {
    const parent = mark.parentNode

    if (!parent) {
      return
    }

    parent.replaceChild(document.createTextNode(mark.textContent ?? ''), mark)

    parent.normalize()
  })
}

function highlightText(container: HTMLElement, query: string) {
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escapedQuery})`, 'gi')

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)

  const matchingNodes: Text[] = []
  let currentNode: Node | null

  while ((currentNode = walker.nextNode())) {
    const text = currentNode.textContent ?? ''

    if (regex.test(text)) {
      matchingNodes.push(currentNode as Text)
    }

    regex.lastIndex = 0
  }

  for (const textNode of matchingNodes) {
    const text = textNode.textContent ?? ''
    const fragment = document.createDocumentFragment()

    let lastIndex = 0
    let match: RegExpExecArray | null

    regex.lastIndex = 0

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)))
      }

      const mark = document.createElement('mark')
      mark.className = 'search-highlight'
      mark.textContent = match[0]
      mark.style.padding = '0 1px'
      mark.style.color = 'var(--color-primary)'
      mark.style.background = 'var(--color-accent)'
      mark.style.borderRadius = '2px'

      fragment.appendChild(mark)
      lastIndex = regex.lastIndex
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)))
    }

    textNode.parentNode?.replaceChild(fragment, textNode)
  }
}
</script>

<style scoped>
.resize-handle {
  position: absolute;
  inset-block: 0;
  left: 0;
  z-index: 10;
  width: 5px;
  cursor: ew-resize;
  background: var(--color-tertiary);
  transition: background 0.15s;
}

.resize-handle:hover,
.resize-handle:active {
  background: var(--color-accent);
}

.docs-content :deep(details) {
  margin-bottom: 0.2rem;
}

.docs-content :deep(summary) {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.4rem;
  cursor: pointer;
  user-select: none;
  list-style: none;
  border-radius: 0.25rem;
}

.docs-content :deep(summary::-webkit-details-marker),
.docs-content :deep(summary::marker) {
  display: none;
}

.docs-content :deep(summary::before) {
  flex-shrink: 0;
  margin-top: 1px;
  color: var(--color-text) / 35%;
  font-size: 0.58em;
  content: '▶';
  transition: transform 0.15s;
}

.docs-content :deep(details[open] > summary::before) {
  transform: rotate(90deg);
}

.docs-content :deep(summary:hover) {
  background: var(--color-secondary);
}

.docs-content :deep(details > summary > b) {
  color: var(--color-text);
  font-weight: 600;
}

.docs-content :deep(details details > summary > b) {
  color: var(--color-accent);
  font-weight: 600;
}

.docs-content :deep(details p),
.docs-content :deep(details li),
.docs-content :deep(details div) {
  color: var(--color-text);
}

.docs-content :deep(table) {
  width: 100%;
  margin: 0.4rem 0;
  border-collapse: collapse;
  font-size: 0.82em;
}

.docs-content :deep(th) {
  padding: 0.3rem 0.5rem;
  color: var(--color-text);
  font-size: 0.75em;
  font-weight: 600;
  text-align: left;
  background: var(--color-secondary) / 40%;
  border-color: var(--color-tertiary);
}

.docs-content :deep(td) {
  padding: 0.3rem 0.5rem;
  color: var(--color-text);
  vertical-align: top;
  border-color: var(--color-tertiary);
}

.docs-content :deep(code) {
  padding: 0.1em 0.35em;
  color: var(--color-accent);
  font-family: ui-monospace, SFMono-Regular, 'Cascadia Code', monospace;
  font-size: 0.82em;
  white-space: nowrap;
  background: var(--color-secondary) / 60%;
  border-radius: 3px;
}

.docs-content :deep(pre) {
  padding: 0.6rem 0.75rem;
  margin: 0.4rem 0;
  overflow-x: auto;
  background: var(--color-secondary);
  border-color: var(--color-tertiary);
  border-radius: 0.25rem;
}

.docs-content :deep(pre code) {
  padding: 0;
  color: var(--color-text);
  font-size: 0.8em;
  white-space: pre;
  background: none;
}

.docs-content :deep(h4) {
  margin: 0.6rem 0 0.25rem;
  color: var(--color-accent);
  font-size: 0.68em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.docs-content :deep(hr) {
  margin: 0.5rem 0;
  border: none;
  border-top: 1px solid var(--color-tertiary);
}

</style>
